import { observable, freeze, createWatcherFn, WatcherTypeFn } from 'rewire-core';
import { IArea, IGroup, IView, DeepPartial } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import advancedSearchDialogModel from './AdvancedSearchDialogModel';
import mainPageModel from '../main-navigation/MainPageModel';
import scheduleControlsModel from '../schedule/ScheduleControlsModel';
import { notifications } from '../ui/Notifications';
import addAreaDialogModel from './AddAreaDialogModel';
import addGroupDialogModel, { IAddGroupDialogData } from './AddGroupDialogModel';
import areaSettingsDialogModel from './AreaSettingsDialogModel';
import renameAreaDialogModel, { IRenameAreaDialogData } from './RenameAreaDialogModel';
import renameGroupDialogModel, { IRenameGroupDialogData } from './RenameGroupDialogModel';
import duplicateAreaDialogModel, { IDuplicateAreaDialogData } from './DuplicateAreaDialogModel';
import duplicateGroupDialogModel, { IDuplicateGroupDialogData } from './DuplicateGroupDialogModel';
import searchViewDialogModel, { ISearchViewDialogData } from './SearchViewDialogModel';
import archiveDeleteAreaDialogModel from '../settings/ArchiveDeleteAreaDialogModel';
import archiveDeleteGroupDialogModel from '../settings/ArchiveDeleteGroupDialogModel';
import { RouteManagerBase } from '../main-navigation/RouteManager';
import { LQ } from 'rewire-common';

interface ISidePanelContentsModelState {
  // favourites:                IFavourite[];
  // favouritesPanelIsExpanded: boolean;
  areaPanelStates: IAreaPanelStates;
  viewsPanelIsExpanded: boolean;
}

interface IAreaPanelState {
  areaExpanded: boolean;
}

interface IAreaPanelStates {
  [areaId: string]: IAreaPanelState;
}

export class SidePanelContentsModel {
  state: ISidePanelContentsModelState;
  watchAreas: WatcherTypeFn;

  constructor() {
    this.state = observable({
      // favourites:                [],
      // favouritesPanelIsExpanded: false,
      areaPanelStates: {},
      viewsPanelIsExpanded: false,
    });

    archiveDeleteAreaDialogModel.onEvent.addListener('archive', this.reloadAreasAndGroups);
    archiveDeleteAreaDialogModel.onEvent.addListener('delete', this.reloadAreasAndGroups);
    archiveDeleteGroupDialogModel.onEvent.addListener('archive', this.reloadAreasAndGroups);
    archiveDeleteGroupDialogModel.onEvent.addListener('delete', this.reloadAreasAndGroups);
  }

  reset() {
    // this.state.favourites                = [];
    // this.state.favouritesPanelIsExpanded = false;
    this.watchAreas();
    this.state.areaPanelStates = {};
    this.state.viewsPanelIsExpanded = false;
  }

  load = async () => {
    // this.loadFavourites();
    this.loadAreaPanelStates();
    this.watchAreas = createWatcherFn();
    this.watchAreas(
      () => {
        this.areas.length;
      },
      () => this.loadAreaPanelStates(),
    );
  };

  // loadFavourites = async () => {
  //   this.state.favourites = [
  //     {id: 'f1', name: 'Favourite #1', path: '/home/schedule'},
  //     {id: 'f2', name: 'Favourite #2', path: '/home/timecard'},
  //     {id: 'f3', name: 'Favourite #3', path: '/home/areas/a7122b86-858f-4d5b-a4c0-6083b97f0691/'},
  //   ];
  // }

  loadAreaPanelStates = async () => {
    if (!this.areas?.length) {
      return;
    }

    const areaPanelStates: IAreaPanelStates = {};
    for (const area of this.areas) {
      areaPanelStates[area.id!] = { areaExpanded: !!this.areaPanelStates[area.id!]?.areaExpanded };
    }

    freeze(() => {
      this.state.areaPanelStates = areaPanelStates;
    });
  };

  // get favourites(): IFavourite[] {
  //   return this.state.favourites;
  // }

  get areas(): DeepPartial<IArea>[] {
    return mainPageModel.areas;
  }

  get views(): DeepPartial<IView>[] {
    return mainPageModel.views;
  }

  // get favouritesPanelIsExpanded(): boolean {
  //   return this.state.favouritesPanelIsExpanded;
  // }

  get areaPanelStates(): IAreaPanelStates {
    return this.state.areaPanelStates;
  }

  get viewsPanelIsExpanded(): boolean {
    return this.state.viewsPanelIsExpanded;
  }

  addAreaPanelState(areaId: string, expanded = false) {
    this.areaPanelStates[areaId] = { areaExpanded: expanded } as IAreaPanelState;
  }

  removeAreaPanelState(areaId: string) {
    delete this.areaPanelStates[areaId];
  }

  // setFavouritesPanelExpanded(expanded: boolean) {
  //   this.state.favouritesPanelIsExpanded = expanded;
  // }

  setAreaPanelExpanded(areaId: string, expanded: boolean) {
    this.areaPanelStates[areaId].areaExpanded = expanded;
  }

  areaPanelIsExpanded(areaId: string): boolean {
    return !!this.areaPanelStates[areaId]?.areaExpanded;
  }

  setViewsPanelExpanded(expanded: boolean) {
    this.state.viewsPanelIsExpanded = expanded;
  }

  // hasFavourites(): boolean {
  //   return this.favourites && this.favourites.length > 0;
  // }

  hasAreas(): boolean {
    return this.areas && this.areas.length > 0;
  }

  hasGroups(area: IArea): boolean {
    return area?.groups && area.groups.length > 0;
  }

  hasViews(): boolean {
    return this.views && this.views.length > 0;
  }

  showAdvancedSearchDialog() {
    advancedSearchDialogModel.load(scheduleControlsModel.scheduleFilters);
  }

  showRenameAreaDialog = (area: IArea) => {
    const data: IRenameAreaDialogData = {
      area: area,
    };
    renameAreaDialogModel.load(data);
  };

  showRenameGroupDialog = (group: IGroup) => {
    const data: IRenameGroupDialogData = {
      group: group,
    };
    renameGroupDialogModel.load(data);
  };

  showDuplicateAreaDialog = (area: IArea) => {
    const data: IDuplicateAreaDialogData = {
      area: area,
    };
    duplicateAreaDialogModel.load(data);
  };

  showDuplicateGroupDialog = (group: IGroup) => {
    const data: IDuplicateGroupDialogData = {
      group: group,
    };
    duplicateGroupDialogModel.load(data);
  };

  showSearchViewDialog = (view: IView) => {
    const data: ISearchViewDialogData = {
      id: view.id,
      name: view.name,
      fields: view.fields,
      options: view.options,
    };
    searchViewDialogModel.load(data);
  };

  showAddAreaDialog() {
    addAreaDialogModel.load();
  }

  showAddGroupDialog(area: IArea) {
    const addGroupData: IAddGroupDialogData = {
      areaId: area.id,
      areaName: area.name,
    };

    addGroupDialogModel.load(addGroupData);
  }

  showAreaSettingsDialog(area: IArea) {
    areaSettingsDialogModel.load(area.id);
  }

  deleteArea = async (areaId: string) => {
    const area = this.areas.find((a) => a.id === areaId);
    archiveDeleteAreaDialogModel.load({ id: areaId, name: area?.name ?? 'area' }, 'delete');
  };

  archiveArea = async (areaId: string) => {
    const area = this.areas.find((a) => a.id === areaId);
    archiveDeleteAreaDialogModel.load({ id: areaId, name: area?.name ?? 'area' }, 'archive');
  };

  // unarchiveArea = async (areaId: string) => {
  //   const result = await archiveUnArchive('unArchive', 'areas', {id: areaId, name: this.areas.find(a => a.id === areaId)?.name ?? 'area'});
  //   if (result) {
  //     mainPageModel.updateAreasAndGroups();
  //   }
  // };

  deleteGroup = async (groupId: string) => {
    const group = LQ(this.areas)
      .flatMap((a) => a.groups ?? [])
      .filter((g) => g.id === groupId)
      .first();
    archiveDeleteGroupDialogModel.load({ id: groupId, name: group?.name ?? 'group' }, 'delete');
  };

  archiveGroup = async (groupId: string) => {
    const group = LQ(this.areas)
      .flatMap((a) => a.groups ?? [])
      .filter((g) => g.id === groupId)
      .first();
    archiveDeleteGroupDialogModel.load({ id: groupId, name: group?.name ?? 'group' }, 'archive');
  };

  // unarchiveGroup = async (groupId: string) => {
  //   const group = LQ(this.areas).flatMap((a) => a.groups ?? []).filter(g => g.id === groupId).first();
  //   const result = await archiveUnArchive('unArchive', 'groups', {id: groupId, name: group?.name ?? 'group'});
  //   if (result) {
  //     mainPageModel.updateAreasAndGroups();
  //   }
  // };

  reloadAreasAndGroups = async () => {
    setTimeout(() => mainPageModel.updateAreasAndGroups(), 1000);
  };

  deleteSearchView = async (viewId: string) => {
    const result = await DataMutations.deleteViews([viewId]);
    if (result) {
      notifications.success('View Deleted');
      mainPageModel.updateViews();
      if (viewId === RouteManagerBase.current.viewId) {
        RouteManagerBase.current.navigateTo({ viewId: undefined });
      }
    } else {
      notifications.error('View Deletion Failed');
    }
  };

  // removeFavourite(favouriteToRemove: IFavourite) {
  //   const index = this.favourites.findIndex(favourite => favourite.id === favouriteToRemove.id);
  //   if (index > -1) {
  //     this.favourites.splice(index, 1);
  //   }
  // }
}

const sidePanelContentsModel = new SidePanelContentsModel();
export default sidePanelContentsModel;
